import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LogIn, Key } from "lucide-react";

export const LoginButton = ({ variant = "default", size = "default", className = "" }) => {
  const { login, backdoorLogin, loading } = useAuth();
  const [showBackdoor, setShowBackdoor] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [backdoorLoading, setBackdoorLoading] = useState(false);

  const handleBackdoorSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBackdoorLoading(true);
    
    const result = await backdoorLogin(username, password);
    
    if (result.success) {
      setShowBackdoor(false);
      setUsername("");
      setPassword("");
    } else {
      setError(result.error || "Invalid credentials");
    }
    setBackdoorLoading(false);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <Button 
          onClick={login} 
          variant={variant}
          size={size}
          disabled={loading}
          className={className}
          data-testid="login-button"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Sign in with Microsoft
        </Button>
        <Button 
          onClick={() => setShowBackdoor(true)} 
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          data-testid="backdoor-login-trigger"
        >
          <Key className="w-3 h-3 mr-1" />
          Admin Login
        </Button>
      </div>

      <Dialog open={showBackdoor} onOpenChange={setShowBackdoor}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Admin Login</DialogTitle>
            <DialogDescription>
              Enter admin credentials to bypass SSO
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBackdoorSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  data-testid="backdoor-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="backdoor-password"
                />
              </div>
              {error && (
                <p className="text-sm text-red-500" data-testid="backdoor-error">{error}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBackdoor(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={backdoorLoading} data-testid="backdoor-submit">
                {backdoorLoading ? "Signing in..." : "Sign In"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginButton;
